/**
 * Establishes the signed-in customer — on the server, so identity UI is in the
 * first HTML.
 *
 * The `passport_v1` cookie travels with the document request and is deliberately
 * not httpOnly (`shared/utils/auth.ts`), so `store.isLoggedIn` is already correct
 * during SSR and `plugins/fetch-with-token.ts` can put a Bearer on a server-side
 * call. What used to be missing was the customer *object*: it was restored only
 * in the browser, so every server render decided `is_manager` was false and every
 * consumer had to wait for hydration — the manager toolbar and the catalogue's
 * row ticks visibly popped in a beat after the page appeared, dragging the page's
 * bottom padding with them.
 *
 * Fetching here instead means `@pinia/nuxt` serializes the customer into the Nuxt
 * payload, and the client hydrates with it already in place.
 */
export default defineNuxtPlugin({
  name: 'customer-load',
  dependsOn: ['services'],
  async setup() {
    const store = useCustomerStore()

    // Plugins run before route middleware, so `gate.global.ts` has NOT yet had
    // its chance to renew a stale token. It does not need to: `fetch-with-token`
    // refreshes once and replays the call on a 401, so an aged-out access token
    // resolves here rather than costing this render its identity UI.
    //
    // A failure that survives that must not take the render down: a throw inside
    // SSR is a fatal error page, and `logout()` on the server writes a cookie Nuxt
    // only flushes on `app:rendered` — the pair of problems documented at length
    // in `gate.global.ts`. Leave the customer null; the gate runs moments later
    // and redirects a session that is genuinely dead, which does flush the cookie.
    if (store.isLoggedIn && !store.customer) {
      try {
        await store.fetchProfile()
      }
      catch (error) {
        console.error('[customer] profile hydration failed', error)
      }
    }

    if (import.meta.server) return

    // Anything past here is the browser's alone.
    // The customer normally arrives in the payload; this covers the paths that
    // produce none — a failed server fetch, or a client-side entry into a store
    // that was never server-rendered.
    if (store.isLoggedIn && !store.customer) {
      store.fetchProfile().catch((error: { statusCode?: number; status?: number }) => {
        // Only a rejected token ends the session. Logging out on *any* failure
        // means a backend hiccup silently undoes a successful login on the very
        // next page load.
        if ((error?.statusCode ?? error?.status) === 401) store.logout()
        else console.error('[customer] profile hydration failed', error)
      })
    }

    // The customer used to be mirrored into localStorage and read back for an
    // instant first paint. SSR does that job now, and Pinia survives client-side
    // navigation, so the copy is only a second source of truth to drift out of
    // sync. Clear what existing browsers still carry.
    localStorage.removeItem('customer')
  },
})
